import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Attachment } from 'src/app/models/server/attachment';
import { Results } from 'src/app/models/server/core/results-T';
import { FolderDocument } from '../../models/server/folder-document';
import { ConnectedUsersStateService } from '../state/connected-users-state.service';
import { ServiceBase } from './service-base';

@Injectable({
    providedIn: 'root',
})
export class FolderDocumentsService extends ServiceBase {
    /**
     * Constructeur
     * */
    constructor(
        private http: HttpClient,
        public override connectedUserStateService: ConnectedUsersStateService
    ) {
        super(connectedUserStateService);
    }

    /**
     * Récupère les documents d'un dossier
     * @param folderId: Id du dossier
     */
    public list(viewId: number, folderId: string): Observable<FolderDocument[]> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http.get<Results<FolderDocument>>(this.baseUrl + `/api/organizations/${this.organizationId}/folders/${folderId}/documents`, httpOptions).pipe(
            map((x) => {
                x.value.forEach((y) => {
                    y.creationDate = new Date(y.creationDate);
                    y.updatedDate = new Date(y.updatedDate);
                    y.viewId = viewId;
                });
                return x.value;
            })
        );
    }

    /**
     * Ajoute un document à un dossier
     * @param folderId: Identifiant du dossier.
     * @param document: Instance du document.
     */
    public add(folderId: string, document: FolderDocument): Observable<boolean> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http.post(this.baseUrl + `/api/organizations/${this.organizationId}/folders/${folderId}/documents`, document, httpOptions).pipe(
            map((x) => {
                return true;
            })
        );
    }

    /**
     * Supprime un document à un dossier
     * @param folderId: Identifiant du dossier.
     * @param document: Instance du document.
     */
    public delete(viewId: number, folderId: string, documentId: string): Observable<boolean> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http.delete(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/folders/${folderId}/documents/${documentId}`, httpOptions).pipe(
            map((x) => {
                return true;
            })
        );
    }

    /**
     * Met à jour un document à un dossier.
     * @param folderId Identifiant du dossier.
     * @param documentId Identifiant du document.
     * @param document Instance du document.
     */
    public markIsRead(viewId: number, folderId: string, documentId: string, isRead: boolean): Observable<FolderDocument> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http
            .put<FolderDocument>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/folders/${folderId}/documents/${documentId}/Read/${isRead}`, httpOptions)
            .pipe(
                map((x) => {
                    x.creationDate = new Date(x.creationDate);
                    x.updatedDate = new Date(x.updatedDate);
                    x.viewId = viewId;
                    return x;
                })
            );
    }

    /**
     * Modifie l'attribut 'Favorite' d'une pièce-jointe dans un document.
     * @param viewId Id de la vue.
     * @param folderId Identifiant du dossier.
     * @param documentId Identifiant du document.
     * @param document Instance du document.
     * @param attachmentId Id de la pièce-jointe.
     * @param isFavorite Valeur indiquant si la pièce-jointe est ajoutée aux favoris (True) ou retirer des favoris (False).
     */
    public markAttachmentAsFavorite(viewId: number, folderId: string, documentId: string, attachmentId: string, isFavorite: boolean): Observable<Attachment> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http
            .put<Attachment>(
                this.baseUrl +
                    `/api/organizations/${this.organizationId}/views/${viewId}/folders/${folderId}/documents/${documentId}/attachments/${attachmentId}/Favorite/${isFavorite}`,
                httpOptions
            )
            .pipe(
                map((x) => {
                    return Attachment.from(x);
                })
            );
    }
}
